import type { AudioFrame } from "@/utils/arcade/beat";
import type { ArcadeTheme } from "@/utils/arcade/theme";

// Night Drive's rules, kept apart from drawing so the 2D fallback and the
// WebGL renderer run the exact same race, and tests can drive it headless.
//
// Units: z runs along the road in world units (SEGMENT per road row), x runs
// across it in road half widths (-1 and 1 are the edges). Every size below is
// the size the views draw, so what you see is what you hit.

export const SEGMENT = 200;
export const DRAW_DISTANCE = 150;
export const ROAD_WIDTH = 2000;
export const LANES = [-0.62, 0, 0.62];
export const SECTOR_METERS = 2500;
export const SECTOR_LENGTH = SECTOR_METERS * 100;
export const MAX_LIVES = 3;
// The player's car sits this far ahead of the camera; collisions happen there.
export const PLAYER_AHEAD = SEGMENT * 3;
export const CRUISE = SEGMENT * 58;
// New things appear this far ahead of the camera. The views fog everything
// solid from FOG_END on, so nothing pops in: it fades out of the haze.
export const SPAWN_AHEAD = SEGMENT * 140;
export const FOG_START = SEGMENT * 45;
export const FOG_END = SPAWN_AHEAD - SEGMENT * 6;
export const START_TIME = 34;
export const ROAD_LIMIT = 1.08;
// Visual yaw of the player's car when the wheel is held hard over, radians.
export const YAW_MAX = 0.32;
// The player's car pivots on its rear axle, this far in front of the rear bumper.
export const REAR_AXLE = 340;
// Leave a car's lane with less time than this before you would hit it and
// it counts as a close call, worth more than a near miss.
export const CLOSE_CALL = 0.2;
// Start a swerve within this many milliseconds after a beat (or a hair
// before it), past something in your lane, and it scores as on the beat.
export const BEAT_WINDOW = 75;
export const BEAT_EARLY = 35;

export type CarKind = "coupe" | "truck" | "racer";
export type HazardKind = "cone" | "barrier" | "roadworks" | "slick" | "pedestrian";
export type MissionKind = "pass" | "near" | "notes" | "clean" | "speed" | "roadworks" | "crosswalk";
export type MissionReward = "nitro" | "life" | "time";

export interface Footprint {
  halfWidth: number;
  halfLength: number;
}

export const PLAYER_SIZE: Footprint = { halfWidth: 0.225, halfLength: 880 };
export const CAR_SIZE: Record<CarKind, Footprint> = {
  coupe: { halfWidth: 0.225, halfLength: 880 },
  racer: { halfWidth: 0.235, halfLength: 920 },
  truck: { halfWidth: 0.3, halfLength: 1500 },
};
export const HAZARD_SIZE: Record<Exclude<HazardKind, "roadworks">, Footprint> = {
  cone: { halfWidth: 0.06, halfLength: 60 },
  barrier: { halfWidth: 0.21, halfLength: 70 },
  slick: { halfWidth: 0.2, halfLength: 420 },
  pedestrian: { halfWidth: 0.05, halfLength: 60 },
};
// A closed lane covers the lane between its two rows of cones.
export const ROADWORKS_HALF_WIDTH = 0.27;

export interface DriveCar extends Footprint {
  id: number;
  z: number;
  x: number;
  lane: number;
  targetX: number;
  speed: number;
  cruise: number;
  color: string;
  kind: CarKind;
  yaw: number;
  passed: boolean;
  nearMissed: boolean;
  hit: boolean;
  closest: number;
  // Right in front of the player last frame, and whether they swerved out
  // of its way at the last moment.
  ahead: boolean;
  closeCall: boolean;
  // Seconds to spare when the player swerved out, for the close call bonus.
  closeTtc: number;
  // A lane change is signalled (the indicator blinks) before the car moves.
  signal: number;
  signalLane: number;
  laneTimer: number;
  hurry: number;
  knock: number;
  // Speed change per second last frame; the views light the brake lights with it.
  accel: number;
}

export interface DrivePickup {
  id: number;
  z: number;
  x: number;
  taken: boolean;
}

export interface DriveHazard extends Footprint {
  id: number;
  kind: HazardKind;
  z: number;
  x: number;
  // Pedestrians walk across at vx (road half widths a second); a knocked
  // cone tumbles away with vx and vz.
  vx: number;
  vz: number;
  hit: boolean;
  // Slick: 0 oil, 1 puddle. Pedestrian: look. Roadworks: the closed lane.
  variant: number;
  // Pedestrian: their crosswalk. Roadworks: 1 once the player is past it.
  group: number;
  phase: number;
  lift: number;
  // A pedestrian standing at the edge of a lane, waiting for a car to pass.
  waiting: boolean;
}

export type SignKind = "roadworks" | "merge" | "crosswalk";

export interface DriveSign {
  id: number;
  z: number;
  side: number;
  kind: SignKind;
}

export interface DriveCrosswalk {
  id: number;
  z: number;
  hit: boolean;
  done: boolean;
}

export interface DriveMission {
  kind: MissionKind;
  target: number;
  progress: number;
  done: boolean;
  failed: boolean;
  reward: MissionReward;
  points: number;
  kmh: number;
}

export type CrashCause = "car" | "barrier" | "roadworks" | "pedestrian";

export type DriveEvent =
  | { type: "crash"; cause: CrashCause }
  | { type: "bump" }
  | { type: "slide" }
  | { type: "pickup"; gain: number; x: number }
  | { type: "near-miss"; gain: number; side: number; close: boolean }
  | { type: "beat-swerve"; gain: number; side: number }
  | { type: "boost" }
  | { type: "checkpoint"; sector: number; bonus: number; intro: string | null }
  | { type: "mission-done"; label: string; reward: MissionReward; points: number }
  | { type: "mission-missed"; label: string }
  | { type: "mission-new"; label: string }
  | { type: "time-warning"; seconds: number }
  | { type: "timeout" };

export interface DriveState {
  time: number;
  position: number;
  speed: number;
  playerX: number;
  // Smoothed steering (-1..1), how long it has been held hard (0..1), and the
  // car's drawn yaw and body roll.
  steer: number;
  lean: number;
  yaw: number;
  roll: number;
  aimX: number | null;
  braking: boolean;
  lives: number;
  score: number;
  combo: number;
  nitro: number;
  boost: number;
  sector: number;
  timeLeft: number;
  invulnerable: number;
  stun: number;
  spinDir: number;
  slide: number;
  slideVel: number;
  over: boolean;
  endReason: "crash" | "time" | null;
  cars: DriveCar[];
  pickups: DrivePickup[];
  hazards: DriveHazard[];
  signs: DriveSign[];
  crosswalks: DriveCrosswalk[];
  mission: DriveMission | null;
  missionsDone: number;
  crashes: number;
  bumps: number;
  passedCars: number;
  nearMisses: number;
  beatSwerves: number;
  notes: number;
  events: DriveEvent[];
}

// What the player asks for this frame, already translated from keys, pointer
// and taps by the engine.
export interface DriveControls {
  steer: number;
  aim: number | null;
  boost: boolean;
  brake: boolean;
}

export interface DriveSim {
  state: DriveState;
  curveAt(index: number): number;
  hillAt(index: number): number;
  archAt(index: number): boolean;
  update(dt: number, controls: DriveControls, audio: AudioFrame): void;
  autopilot(audio?: AudioFrame): DriveControls;
  speedKmh(): number;
  kmhFor(speed: number): number;
  levelSpeed(): number;
  intensity(): number;
  missionLabel(mission: DriveMission): string;
}

// What each sector adds, announced at its checkpoint.
export const UNLOCKS: Array<{ sector: number; hazards: HazardKind[]; intro: string }> = [
  { sector: 1, hazards: ["cone"], intro: "Dodge the cones" },
  { sector: 2, hazards: ["slick"], intro: "New: oil and puddles make you slide" },
  { sector: 3, hazards: ["pedestrian"], intro: "New: people crossing at crosswalks" },
  { sector: 4, hazards: ["roadworks", "barrier"], intro: "New: roadworks close a lane" },
  { sector: 5, hazards: [], intro: "New: racers weave between lanes" },
];

const unlockedAt = (kind: HazardKind) => UNLOCKS.find((unlock) => unlock.hazards.includes(kind))?.sector ?? 1;

const REWARD_LABEL: Record<MissionReward, string> = { nitro: "full nitro", life: "+1 life", time: "+6 s" };

// Separating axis test for two rectangles on the road plane, in world units.
// Yaw turns a rectangle's nose toward +x.
export function footprintsOverlap(
  ax: number,
  az: number,
  ahw: number,
  ahl: number,
  ayaw: number,
  bx: number,
  bz: number,
  bhw: number,
  bhl: number,
  byaw: number,
  slack = 0,
) {
  const dx = bx - ax;
  const dz = bz - az;
  const sa = Math.sin(ayaw);
  const ca = Math.cos(ayaw);
  const sb = Math.sin(byaw);
  const cb = Math.cos(byaw);
  // Axes: each rectangle's right (cos, -sin) and forward (sin, cos).
  const axes = [ca, -sa, sa, ca, cb, -sb, sb, cb];
  for (let i = 0; i < 8; i += 2) {
    const nx = axes[i];
    const nz = axes[i + 1];
    const ra = ahw * Math.abs(ca * nx - sa * nz) + ahl * Math.abs(sa * nx + ca * nz);
    const rb = bhw * Math.abs(cb * nx - sb * nz) + bhl * Math.abs(sb * nx + cb * nz);
    if (Math.abs(dx * nx + dz * nz) >= ra + rb - slack) return false;
  }
  return true;
}

// Where the player's car is on the road: its centre, after turning on the rear axle.
export function playerFootprint(state: DriveState) {
  const pivotZ = state.position + PLAYER_AHEAD - PLAYER_SIZE.halfLength + REAR_AXLE;
  const arm = PLAYER_SIZE.halfLength - REAR_AXLE;
  return {
    x: state.playerX * ROAD_WIDTH + Math.sin(state.yaw) * arm,
    z: pivotZ + Math.cos(state.yaw) * arm,
    yaw: state.yaw,
  };
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const smooth = (t: number) => t * t * (3 - 2 * t);

export function createDriveSim(theme: ArcadeTheme, rng: () => number, difficulty: number): DriveSim {
  const seeds = [rng() * 100, rng() * 100, rng() * 100];
  let ids = 0;
  let beatCount = 0;
  let trafficBudget = 0.6;
  let nextHazardZ = SPAWN_AHEAD + SEGMENT * 20;
  let lastMission: MissionKind | null = null;
  let hitThisSector = false;
  let speedStreak = 0;
  let lastTarget = -1;
  let lastKeySteer = 0;
  let swerveCooldown = 0;
  let lastWarning = 99;
  let forced: HazardKind[] = [];
  const pending: Array<{ z: number; run: () => void }> = [];
  const state: DriveState = {
    time: 0,
    position: 0,
    speed: 0,
    playerX: 0,
    steer: 0,
    lean: 0,
    yaw: 0,
    roll: 0,
    aimX: null,
    braking: false,
    lives: MAX_LIVES,
    score: 0,
    combo: 0,
    nitro: 0.35,
    boost: 0,
    sector: 1,
    timeLeft: START_TIME,
    invulnerable: 0,
    stun: 0,
    spinDir: 1,
    slide: 0,
    slideVel: 0,
    over: false,
    endReason: null,
    cars: [],
    pickups: [],
    hazards: [],
    signs: [],
    crosswalks: [],
    mission: null,
    missionsDone: 0,
    crashes: 0,
    bumps: 0,
    passedCars: 0,
    nearMisses: 0,
    beatSwerves: 0,
    notes: 0,
    events: [],
  };

  const curveAt = (index: number) => Math.sin(index * 0.021 + seeds[0]) * 3.2 + Math.sin(index * 0.0063 + seeds[1]) * 2.4;
  const hillAt = (index: number) => Math.sin(index * 0.017 + seeds[2]) * 1400 + Math.sin(index * 0.041) * 400;
  const archAt = (index: number) => state.sector >= 2 && index % 24 === 0;
  const unlocked = (kind: HazardKind) => state.sector >= unlockedAt(kind);

  // Speed climbs with every sector, up to sector 8.
  const levelFactor = () => 1 + 0.07 * (Math.min(state.sector, 8) - 1);
  const levelSpeed = () => CRUISE * difficulty * levelFactor();
  // Points multiply with a clean streak: x2 after 12 in a row, up to x4.
  // Any hit starts it again, so steady driving pays.
  const multiplier = () => Math.min(4, 1 + Math.floor(state.combo / 12));

  // Traffic and hazards also thicken with time on the road, so a slow run
  // does not stay easy forever.
  const pressure = () => Math.max(state.sector, 1 + state.time / 20);
  const trafficDensity = () => Math.min(0.9, 0.27 + 0.08 * (pressure() - 1));
  const kmhFor = (speed: number) => Math.round((speed / CRUISE) * 180);
  const sectorBonus = (sector: number) => {
    const speed = CRUISE * difficulty * (1 + 0.07 * (Math.min(sector, 8) - 1));
    return Math.round((SECTOR_LENGTH / speed) * 1.4 + 3);
  };

  const missionLabel = (mission: DriveMission) => {
    switch (mission.kind) {
      case "pass":
        return `Pass ${mission.target} cars`;
      case "near":
        return `${mission.target} near misses`;
      case "notes":
        return `Collect ${mission.target} notes`;
      case "clean":
        return "No hits this sector";
      case "speed":
        return `Hold ${mission.kmh}+ km/h for ${mission.target}s`;
      case "roadworks":
        return "Pass the roadworks clean";
      case "crosswalk":
        return `Clear ${mission.target} crosswalks safely`;
    }
  };

  const newMission = () => {
    const pool: MissionKind[] = state.sector === 1 ? ["pass", "notes"] : ["pass", "near", "notes", "speed", "clean"];
    if (unlocked("pedestrian")) pool.push("crosswalk");
    if (unlocked("roadworks")) pool.push("roadworks", "roadworks");
    const choices = pool.filter((kind) => kind !== lastMission);
    const kind = choices[Math.floor(rng() * choices.length)];
    lastMission = kind;
    const s = state.sector;
    const target =
      kind === "pass"
        ? Math.min(18, 6 + 2 * (s - 1))
        : kind === "near"
          ? Math.min(4, 2 + Math.floor(s / 3))
          : kind === "notes"
            ? Math.min(20, 8 + 2 * s)
            : kind === "speed"
              ? 2
              : kind === "crosswalk"
                ? 2
                : 1;
    const roll = rng();
    const reward: MissionReward = state.lives < MAX_LIVES && roll < 0.35 ? "life" : roll < 0.7 ? "nitro" : "time";
    state.mission = { kind, target, progress: 0, done: false, failed: false, reward, points: 300 + 100 * s, kmh: Math.floor((kmhFor(levelSpeed()) * 1.12) / 10) * 10 };
    hitThisSector = false;
    speedStreak = 0;
    // The roadworks and crosswalk missions bring their own hazards.
    if (kind === "roadworks") forced.push("roadworks");
    if (kind === "crosswalk") forced.push("pedestrian", "pedestrian");
    state.events.push({ type: "mission-new", label: missionLabel(state.mission) });
  };

  const advanceMission = (kind: MissionKind, amount = 1) => {
    const mission = state.mission;
    if (!mission || mission.done || mission.failed || mission.kind !== kind) return;
    mission.progress = Math.min(mission.target, mission.progress + amount);
    if (mission.progress >= mission.target) completeMission();
  };

  const completeMission = () => {
    const mission = state.mission;
    if (!mission || mission.done) return;
    mission.done = true;
    mission.progress = mission.target;
    state.missionsDone += 1;
    state.score += mission.points;
    if (mission.reward === "life") state.lives = Math.min(MAX_LIVES, state.lives + 1);
    else if (mission.reward === "nitro") state.nitro = 1;
    else state.timeLeft += 6;
    state.events.push({ type: "mission-done", label: missionLabel(mission), reward: mission.reward, points: mission.points });
  };

  const failMission = () => {
    const mission = state.mission;
    if (!mission || mission.done || mission.failed) return;
    mission.failed = true;
    state.events.push({ type: "mission-missed", label: missionLabel(mission) });
  };

  // --- Spawning -------------------------------------------------------------

  const spawnZ = () => state.position + SPAWN_AHEAD;
  const lateralHit = (a: number, aw: number, b: number, bw: number, margin: number) => Math.abs(a - b) < aw + bw + margin;

  // Crosswalks planned or on the road, so traffic and other hazards keep clear of them.
  const walkPlan: number[] = [];
  const nearCrosswalk = (z: number, reach: number) => walkPlan.some((at) => Math.abs(at - z) < reach);

  // Is this lane free around z of cars and hazards (pedestrians included)?
  const laneFree = (x: number, halfWidth: number, z: number, halfLength: number, margin: number) => {
    for (const car of state.cars) {
      if (!lateralHit(car.x, car.halfWidth, x, halfWidth, 0.06) && !lateralHit(car.targetX, car.halfWidth, x, halfWidth, 0.06)) continue;
      if (Math.abs(car.z - z) < car.halfLength + halfLength + margin) return false;
    }
    for (const hazard of state.hazards) {
      if (!lateralHit(hazard.x, hazard.halfWidth, x, halfWidth, 0.06)) continue;
      if (Math.abs(hazard.z - z) < hazard.halfLength + halfLength + margin) return false;
    }
    return true;
  };

  // How many lanes are blocked near z. Spawns never close all three.
  const blockedLanes = (z: number, reach: number, extraLane = -1) => {
    let blocked = 0;
    for (let lane = 0; lane < LANES.length; lane++) {
      if (lane === extraLane || !laneFree(LANES[lane], 0.2, z, 0, reach)) blocked += 1;
    }
    return blocked;
  };

  const spawnCar = () => {
    const z = spawnZ();
    if (nearCrosswalk(z, 5200)) return false;
    const roll = rng();
    const kind: CarKind = unlocked("slick") && roll < 0.22 ? "truck" : state.sector >= 5 && roll > 0.8 ? "racer" : "coupe";
    const size = CAR_SIZE[kind];
    const start = Math.floor(rng() * LANES.length);
    for (let k = 0; k < LANES.length; k++) {
      const lane = (start + k) % LANES.length;
      if (!laneFree(LANES[lane], size.halfWidth, z, size.halfLength, 1400)) continue;
      if (blockedLanes(z, 3400, lane) >= LANES.length) continue;
      const base = levelSpeed();
      const cruise = base * (kind === "truck" ? 0.3 + rng() * 0.07 : kind === "racer" ? 0.62 + rng() * 0.08 : 0.38 + rng() * 0.2);
      const colors = theme.palette.lanes;
      state.cars.push({
        id: ids++,
        z,
        x: LANES[lane],
        lane,
        targetX: LANES[lane],
        speed: cruise,
        cruise,
        color: colors[Math.floor(rng() * colors.length)],
        kind,
        ...size,
        yaw: 0,
        passed: false,
        nearMissed: false,
        hit: false,
        closest: Infinity,
        ahead: false,
        closeCall: false,
        closeTtc: 1,
        signal: 0,
        signalLane: lane,
        laneTimer: 2 + rng() * 3,
        hurry: 0,
        knock: 0,
        accel: 0,
      });
      return true;
    }
    return false;
  };

  const spawnNotes = () => {
    const z = spawnZ();
    if (nearCrosswalk(z, 4000)) return;
    const lane = Math.floor(rng() * LANES.length);
    const count = 3 + Math.floor(rng() * 2);
    for (let i = 0; i < count; i++) {
      const at = z + i * 700;
      if (!laneFree(LANES[lane], 0.1, at, 60, 200)) continue;
      state.pickups.push({ id: ids++, z: at, x: LANES[lane], taken: false });
    }
  };

  const addHazard = (kind: HazardKind, x: number, z: number, size: Footprint, variant = 0, group = 0, vx = 0) => {
    state.hazards.push({ id: ids++, kind, z, x, halfWidth: size.halfWidth, halfLength: size.halfLength, vx, vz: 0, hit: false, variant, group, phase: rng() * 6, lift: 0, waiting: false });
  };

  // Would a car in this lane reach z before the player does? Then it would
  // have to queue behind the hazard, so the hazard goes elsewhere.
  const trafficReaches = (x: number, z: number) => {
    const playerZ = state.position + PLAYER_AHEAD;
    for (const car of state.cars) {
      if (!lateralHit(car.x, car.halfWidth, x, 0.25, 0.04) && !lateralHit(car.targetX, car.halfWidth, x, 0.25, 0.04)) continue;
      if (car.z > playerZ + (z - playerZ) * 0.4) return true;
    }
    return false;
  };

  // Will this lane still have traffic around z when the player gets there?
  // Cars move on while the player closes in, so look where they will be.
  const busyOnArrival = (lane: number, z: number, halfLength: number) => {
    const playerZ = state.position + PLAYER_AHEAD;
    const eta = (z - playerZ) / Math.max(levelSpeed(), state.speed);
    const x = LANES[lane];
    for (const car of state.cars) {
      if (!lateralHit(car.x, car.halfWidth, x, 0.22, 0.04) && !lateralHit(car.targetX, car.halfWidth, x, 0.22, 0.04)) continue;
      if (Math.abs(car.z + car.speed * eta - z) < car.halfLength + halfLength + 2600) return true;
    }
    return !laneFree(x, 0.22, z, halfLength, 1200);
  };

  // A free lane for a hazard, from a random start, or -1. Traffic in the
  // other lanes must leave at least one of them open when the player arrives.
  const pickLane = (z: number, halfLength: number) => {
    const start = Math.floor(rng() * LANES.length);
    for (let k = 0; k < LANES.length; k++) {
      const lane = (start + k) % LANES.length;
      if (trafficReaches(LANES[lane], z)) continue;
      if (!laneFree(LANES[lane], 0.3, z, halfLength, 900)) continue;
      let open = 0;
      for (let other = 0; other < LANES.length; other++) if (other !== lane && !busyOnArrival(other, z, halfLength)) open += 1;
      if (open >= 1) return lane;
    }
    return -1;
  };

  // Stretches of road already closed in one lane. Nothing else that closes a
  // lane goes within a few car lengths of them, so two closures can never
  // form a staircase that traps the player.
  const closures: Array<[number, number]> = [];
  const closureFree = (from: number, to: number) => closures.every(([a, b]) => b < from - 5000 || a > to + 5000);

  const spawnCones = (z: number) => {
    if (!closureFree(z, z + 1600)) return false;
    const lane = pickLane(z, 1800);
    if (lane < 0) return false;
    const x = LANES[lane];
    if (rng() < 0.5) {
      // A short fence of cones across the lane.
      for (const offset of [-0.17, 0, 0.17]) addHazard("cone", x + offset, z, HAZARD_SIZE.cone);
    } else {
      // A taper from the lane edge to its middle.
      const side = x === 0 ? (rng() < 0.5 ? -1 : 1) : Math.sign(x);
      for (let i = 0; i < 4; i++) addHazard("cone", x + side * (0.26 - i * 0.12), z + i * 520, HAZARD_SIZE.cone);
    }
    closures.push([z, z + 1600]);
    return true;
  };

  const spawnSlick = (z: number) => {
    const lane = pickLane(z, HAZARD_SIZE.slick.halfLength);
    if (lane < 0) return false;
    addHazard("slick", LANES[lane] + (rng() - 0.5) * 0.12, z, HAZARD_SIZE.slick, rng() < 0.5 ? 0 : 1);
    return true;
  };

  const spawnBarrier = (z: number) => {
    if (!closureFree(z - 500, z)) return false;
    const lane = pickLane(z, 600);
    if (lane < 0) return false;
    const x = LANES[lane];
    addHazard("barrier", x, z, HAZARD_SIZE.barrier);
    addHazard("cone", x - 0.26, z - 500, HAZARD_SIZE.cone);
    addHazard("cone", x + 0.26, z - 500, HAZARD_SIZE.cone);
    closures.push([z - 500, z]);
    return true;
  };

  // Signs now, the crosswalk and its people later, once the signs are in view.
  const spawnCrosswalk = (z: number) => {
    if (nearCrosswalk(z, 16000) || !closureFree(z + 7000, z + 7000)) return false;
    const group = ids++;
    for (const side of [-1, 1]) state.signs.push({ id: ids++, z, side, kind: "crosswalk" });
    const at = z + 7000;
    walkPlan.push(at);
    pending.push({
      z: at,
      run: () => {
        state.crosswalks.push({ id: group, z: at, hit: false, done: false });
        const from = rng() < 0.5 ? -1 : 1;
        const people = state.sector >= 5 && rng() < 0.6 ? 2 : 1;
        for (let i = 0; i < people; i++) {
          // Two people cross together from opposite kerbs, or one after the other.
          const start = i === 1 && rng() < 0.5 ? -from : from;
          const speed = 0.36 + rng() * 0.16;
          addHazard("pedestrian", start * (1.22 + i * 0.12), at + (i - 0.5) * 140 * (people - 1), HAZARD_SIZE.pedestrian, Math.floor(rng() * 4), group, -start * speed);
        }
      },
    });
    return true;
  };

  // Warning signs, a merge sign, then the closed lane.
  const spawnRoadworks = (z: number) => {
    const lane = Math.floor(rng() * LANES.length);
    const start = z + 8000;
    const length = 4600 + rng() * 3000 + Math.min(state.sector, 8) * 300;
    if (!closureFree(start, start + length + 3000) || nearCrosswalk(start, length + 9000)) return false;
    closures.push([start, start + length + 3000]);
    const sides = lane === 1 ? [-1, 1] : [lane === 0 ? -1 : 1];
    for (const side of sides) {
      state.signs.push({ id: ids++, z, side, kind: "roadworks" });
      pending.push({ z: z + 4000, run: () => state.signs.push({ id: ids++, z: z + 4000, side, kind: "merge" }) });
    }
    pending.push({
      z: start,
      run: () => {
        // Never drop the zone onto a car: slide it further out instead.
        let from = start;
        for (const car of state.cars) {
          if (lateralHit(car.x, car.halfWidth, LANES[lane], ROADWORKS_HALF_WIDTH, 0.04) && car.z + car.halfLength > from - 200 && car.z - car.halfLength < from + length) from = car.z + car.halfLength + 400;
        }
        if (from > start + 3000) return;
        state.hazards.push({ id: ids++, kind: "roadworks", z: from + length / 2, x: LANES[lane], halfWidth: ROADWORKS_HALF_WIDTH, halfLength: length / 2, vx: 0, vz: 0, hit: false, variant: lane, group: 0, phase: 0, lift: 0, waiting: false });
      },
    });
    return true;
  };

  const hazardGapBase = () => Math.max(3600, 10500 - 1100 * (pressure() - 1));
  const hazardGap = () => hazardGapBase() * (0.8 + rng() * 0.4);

  const spawnHazard = () => {
    const z = spawnZ();
    if (nearCrosswalk(z, 6000)) return;
    let kind = forced.shift();
    if (!kind) {
      const options: Array<[HazardKind, number]> = [["cone", 3]];
      if (unlocked("slick")) options.push(["slick", 2]);
      if (unlocked("pedestrian")) options.push(["pedestrian", 1.3]);
      if (unlocked("barrier")) options.push(["barrier", 1.6], ["roadworks", 1.1]);
      let total = 0;
      for (const [, weight] of options) total += weight;
      let pick = rng() * total;
      kind = options[options.length - 1][0];
      for (const [option, weight] of options) {
        pick -= weight;
        if (pick <= 0) {
          kind = option;
          break;
        }
      }
    }
    const placed =
      kind === "cone" ? spawnCones(z) : kind === "slick" ? spawnSlick(z) : kind === "barrier" ? spawnBarrier(z) : kind === "pedestrian" ? spawnCrosswalk(z) : spawnRoadworks(z);
    if (!placed && kind !== "cone") forced.unshift(kind);
  };

  // --- Collisions -------------------------------------------------------------

  const crash = (cause: CrashCause) => {
    state.lives -= 1;
    state.crashes += 1;
    state.combo = 0;
    state.invulnerable = 2.2;
    state.stun = 0.8;
    state.spinDir = rng() < 0.5 ? -1 : 1;
    state.speed *= 0.25;
    state.boost = 0;
    state.slide = 0;
    state.slideVel = 0;
    hitThisSector = true;
    if (state.mission?.kind === "clean") failMission();
    state.events.push({ type: "crash", cause });
    if (state.lives <= 0) {
      state.over = true;
      state.endReason = "crash";
    }
  };

  const touches = (thing: Footprint & { x: number; z: number }, yaw = 0, slack = 30) => {
    const player = playerFootprint(state);
    return footprintsOverlap(player.x, player.z, PLAYER_SIZE.halfWidth * ROAD_WIDTH, PLAYER_SIZE.halfLength, player.yaw, thing.x * ROAD_WIDTH, thing.z, thing.halfWidth * ROAD_WIDTH, thing.halfLength, yaw, slack);
  };

  // --- Traffic ----------------------------------------------------------------

  const updateTraffic = (dt: number, playerZ: number) => {
    for (const car of state.cars) {
      // Nearest thing ahead in this car's path: another car, a hazard or a person.
      let gap = Infinity;
      let leaderSpeed = car.cruise;
      let staticAhead = false;
      for (const other of state.cars) {
        if (other === car || other.z <= car.z) continue;
        // Only what actually overlaps this car's path: trucks in the next lane come close.
        if (!lateralHit(other.x, other.halfWidth, car.x, car.halfWidth, -0.02) && !lateralHit(other.x, other.halfWidth, car.targetX, car.halfWidth, -0.02)) continue;
        const g = other.z - other.halfLength - (car.z + car.halfLength);
        if (g < gap) {
          gap = g;
          leaderSpeed = other.speed;
          staticAhead = other.speed < car.cruise * 0.2;
        }
      }
      for (const hazard of state.hazards) {
        if (hazard.kind === "slick" || hazard.hit || hazard.z <= car.z) continue;
        const reach = hazard.kind === "pedestrian" ? 0.2 : -0.02;
        if (!lateralHit(hazard.x, hazard.halfWidth, car.x, car.halfWidth, reach) && !lateralHit(hazard.x, hazard.halfWidth, car.targetX, car.halfWidth, reach)) continue;
        const g = hazard.z - hazard.halfLength - (car.z + car.halfLength);
        if (g < gap) {
          gap = g;
          leaderSpeed = 0;
          staticAhead = hazard.kind !== "pedestrian";
        }
      }

      // Merge out of a blocked lane, and racers weave on their own.
      car.laneTimer -= dt;
      const settled = Math.abs(car.targetX - car.x) < 0.01;
      // Every lane change is signalled first; the car moves when the
      // indicator has blinked for a moment, if the gap is still there.
      // Nobody cuts in on the player, except racers from sector 5 on, and
      // even they signal first and leave some room.
      const cutsIn = (x: number) => {
        const aim = state.aimX ?? state.playerX;
        const nearPlayer = Math.abs(x - state.playerX) < 0.7 || Math.abs(x - aim) < 0.7;
        const racerCut = car.kind === "racer" && state.sector >= 5 && car.z - playerZ > 3800;
        // Only matters while the player could reach the gap soon.
        const soon = car.z - playerZ < Math.max(0, state.speed - car.speed) * 1.6 + 2500;
        return car.z > playerZ - 3000 && soon && nearPlayer && !racerCut;
      };
      if (car.signal > 0) {
        car.signal -= dt;
        if (car.signal <= 0) {
          car.signal = 0;
          const x = LANES[car.signalLane];
          if (laneFree(x, car.halfWidth, car.z, car.halfLength, 600) && !cutsIn(x)) {
            car.lane = car.signalLane;
            car.targetX = x;
          }
        }
      }
      // Out of a blocked lane, or round a slower car so no trains form.
      const wantsOut = (staticAhead && gap < 7000) || (gap < 3500 && leaderSpeed < car.cruise * 0.9);
      if (settled && car.signal <= 0 && (wantsOut || (car.kind === "racer" && car.laneTimer <= 0))) {
        car.laneTimer = 2.5 + rng() * 3;
        const choices = car.lane === 1 ? (rng() < 0.5 ? [0, 2] : [2, 0]) : [1];
        for (const lane of choices) {
          const x = LANES[lane];
          // Only into a real gap, and never where it would close the road.
          if (!laneFree(x, car.halfWidth, car.z, car.halfLength, 900 + car.speed * 0.3)) continue;
          if (cutsIn(x)) continue;
          car.signal = car.kind === "racer" ? 0.55 : 0.8;
          car.signalLane = lane;
          break;
        }
      }

      const safe = 500 + car.speed * 0.35;
      let desired = car.cruise * (car.hurry > 0 ? 1.6 : 1);
      car.hurry = Math.max(0, car.hurry - dt);
      if (gap < safe * 3) desired = Math.min(desired, leaderSpeed + (gap - safe) * 1.6);
      // Cars the player has passed never catch up again.
      if (car.z + car.halfLength < playerZ - PLAYER_SIZE.halfLength) desired = Math.min(desired, state.speed * 0.9);
      desired = Math.max(0, desired);
      const change = clamp(desired - car.speed, -car.cruise * 3.5 * dt, car.cruise * 0.9 * dt);
      car.speed += change;
      car.accel += (change / Math.max(dt, 1e-4) - car.accel) * Math.min(1, dt * 8);
      car.z += car.speed * dt;

      // Cars steer across at a pace that depends on their speed; a stopped car
      // does not slide sideways.
      const dx = car.targetX - car.x;
      const step = Math.sign(dx) * Math.min(Math.abs(dx), dt * 0.7 * Math.max(0.3, Math.min(1, car.speed / 3000)));
      car.x += step + car.knock * dt;
      car.knock *= Math.max(0, 1 - dt * 4);
      const heading = Math.atan2(((step / Math.max(dt, 1e-4)) + car.knock) * ROAD_WIDTH, Math.max(car.speed, 1500));
      car.yaw += (clamp(heading, -0.35, 0.35) - car.yaw) * Math.min(1, dt * 10);
    }

    // Guarantee: no two cars ever overlap, and no car drives through a hazard.
    state.cars.sort((a, b) => a.z - b.z);
    for (let i = state.cars.length - 1; i >= 0; i--) {
      const car = state.cars[i];
      for (let j = i + 1; j < state.cars.length; j++) {
        const front = state.cars[j];
        if (!lateralHit(front.x, front.halfWidth, car.x, car.halfWidth, -0.01)) continue;
        const limit = front.z - front.halfLength - car.halfLength - 150;
        if (car.z > limit) {
          car.z = limit;
          car.speed = Math.min(car.speed, front.speed);
        }
      }
      for (const hazard of state.hazards) {
        if (hazard.kind === "slick" || hazard.hit || hazard.z < car.z - car.halfLength) continue;
        if (!lateralHit(hazard.x, hazard.halfWidth, car.x, car.halfWidth, -0.01)) continue;
        const limit = hazard.z - hazard.halfLength - car.halfLength - 80;
        if (car.z > limit) {
          car.z = limit;
          car.speed = 0;
        }
      }
    }

    // Three lanes taken within a few car lengths is a wall, even staggered:
    // the car at the front of it hurries on and opens a gap.
    for (let i = 0; i < state.cars.length; i++) {
      const a = state.cars[i];
      if (a.z > playerZ + FOG_END || a.z < playerZ) continue;
      let lanes = 0;
      let front = a;
      for (let lane = 0; lane < LANES.length; lane++) {
        const x = LANES[lane];
        let taken = false;
        for (const other of state.cars) {
          if (Math.abs(other.z - a.z) < other.halfLength + a.halfLength + 3200 && lateralHit(other.x, other.halfWidth, x, 0.2, 0.02)) {
            taken = true;
            if (other.z > front.z) front = other;
          }
        }
        if (!taken) {
          for (const hazard of state.hazards) {
            if (hazard.kind !== "slick" && Math.abs(hazard.z - a.z) < hazard.halfLength + a.halfLength + 3200 && lateralHit(hazard.x, hazard.halfWidth, x, 0.2, 0.02)) taken = true;
          }
        }
        if (taken) lanes += 1;
      }
      if (lanes >= LANES.length) front.hurry = 1.6;
    }
  };

  // --- Update -----------------------------------------------------------------

  const update = (dt: number, controls: DriveControls, audio: AudioFrame) => {
    state.events.length = 0;
    if (state.over) return;
    if (!state.mission) newMission();
    state.time += dt;
    state.invulnerable = Math.max(0, state.invulnerable - dt);
    state.boost = Math.max(0, state.boost - dt);
    state.stun = Math.max(0, state.stun - dt);

    if (controls.boost && state.nitro >= 1 && state.boost <= 0 && state.stun <= 0) {
      state.nitro = 0;
      state.boost = 3.2;
      state.events.push({ type: "boost" });
    }

    // Speed: the car drives itself; brake to crawl, nitro to fly.
    state.braking = controls.brake;
    const cruise = levelSpeed() * (0.97 + audio.energy * 0.06) * (state.boost > 0 ? 1.4 : 1);
    const offRoad = Math.abs(state.playerX) > 0.96;
    let limit = offRoad ? cruise * 0.5 : cruise;
    if (controls.brake) limit = 0;
    if (state.stun > 0) limit = 0;
    const rate = state.speed < limit ? (state.boost > 0 ? 2.4 : 1) : controls.brake ? 2.2 : state.stun > 0 ? 3.5 : 2;
    state.speed += (limit - state.speed) * Math.min(1, dt * rate);
    const before = state.position;
    state.position += state.speed * dt;
    const speedPercent = state.speed / CRUISE;

    // Steering: keys steer directly; a pointer sets a place on the road the car
    // holds until the next touch.
    let steer = 0;
    if (controls.steer !== 0) {
      steer = clamp(controls.steer, -1, 1);
      state.aimX = null;
    } else if (controls.aim !== null) state.aimX = clamp(controls.aim, -ROAD_LIMIT, ROAD_LIMIT);
    if (steer === 0 && state.aimX !== null) steer = clamp((state.aimX - state.playerX) * 4, -1, 1);
    const authority = state.stun > 0 ? 0 : state.slide > 0 ? 0.45 : 1;
    const baseIndex = Math.floor(state.position / SEGMENT);
    state.playerX += steer * authority * dt * 2.2 * Math.min(1, speedPercent + 0.25);
    state.playerX -= curveAt(baseIndex) * speedPercent * dt * 0.08;
    if (state.slide > 0) {
      state.slide = Math.max(0, state.slide - dt);
      state.playerX += state.slideVel * dt;
      state.slideVel *= Math.max(0, 1 - dt * 1.6);
    }
    state.playerX = clamp(state.playerX, -ROAD_LIMIT, ROAD_LIMIT);

    // The car only turns its side to the camera when the wheel is held hard.
    state.steer += (steer * authority - state.steer) * Math.min(1, dt * 8);
    const hard = Math.abs(state.steer) > 0.8;
    state.lean = clamp(state.lean + (hard ? dt * 3 : -dt * 4), 0, 1);
    const amount = smooth(clamp((Math.abs(state.steer) - 0.25) / 0.75, 0, 1));
    let yaw = Math.sign(state.steer) * YAW_MAX * amount * amount * (0.45 + 0.55 * state.lean);
    if (state.slide > 0) yaw += Math.sin(state.time * 13) * 0.2 * Math.min(1, state.slide);
    if (state.stun > 0) yaw = state.spinDir * Math.PI * 2 * smooth(1 - state.stun / 0.8);
    state.yaw += (yaw - state.yaw) * Math.min(1, dt * (state.stun > 0 ? 30 : 7));
    state.roll += (state.steer * 0.07 * (0.5 + 0.5 * amount) - state.roll) * Math.min(1, dt * 6);

    // A swerve starts when the car is sent to another lane: a fresh key
    // press, or the aim moving into another lane. On the beat, and past
    // something in the lane it leaves, it scores.
    swerveCooldown = Math.max(0, swerveCooldown - dt);
    const nearestLane = (x: number) => (x < -0.31 ? 0 : x > 0.31 ? 2 : 1);
    let target = -1;
    if (controls.steer !== 0 && lastKeySteer === 0) target = clamp(nearestLane(state.playerX) + Math.sign(controls.steer), 0, 2);
    else if (controls.steer === 0 && state.aimX !== null) target = nearestLane(state.aimX);
    lastKeySteer = controls.steer;
    if (target >= 0 && target !== lastTarget) {
      const from = nearestLane(state.playerX);
      lastTarget = target;
      const onBeat = audio.sinceBeat < BEAT_WINDOW || audio.nextBeatIn < BEAT_EARLY;
      if (target !== from && onBeat && swerveCooldown <= 0 && state.stun <= 0) {
        const playerZ = state.position + PLAYER_AHEAD;
        const x = LANES[from];
        let dodging = false;
        for (const car of state.cars) if (!dodging && !car.hit && car.z > playerZ && car.z - playerZ < 9000 && lateralHit(car.x, car.halfWidth, x, 0.2, 0)) dodging = true;
        for (const hazard of state.hazards) if (!dodging && !hazard.hit && hazard.kind !== "slick" && hazard.z > playerZ && hazard.z - hazard.halfLength - playerZ < 9000 && lateralHit(hazard.x, hazard.halfWidth, x, 0.2, 0)) dodging = true;
        if (dodging) {
          swerveCooldown = 0.35;
          state.beatSwerves += 1;
          state.combo += 1;
          const gain = 220 * multiplier();
          state.score += gain;
          state.nitro = Math.min(1, state.nitro + 0.06);
          state.events.push({ type: "beat-swerve", gain, side: Math.sign(LANES[target] - x) });
        }
      }
    }

    // Checkpoints: more time, a new mission, and anything new announced.
    const sector = 1 + Math.floor(state.position / SECTOR_LENGTH);
    if (sector > state.sector) {
      if (state.mission?.kind === "clean" && !hitThisSector) completeMission();
      else if (state.mission && !state.mission.done) failMission();
      state.sector = sector;
      const bonus = sectorBonus(sector);
      state.timeLeft += bonus;
      state.score += 250 * (sector - 1);
      const unlock = UNLOCKS.find((item) => item.sector === sector);
      if (unlock) forced.push(...unlock.hazards);
      if (unlock && unlock.hazards.length) nextHazardZ = Math.min(nextHazardZ, spawnZ() + SEGMENT * 10);
      state.events.push({ type: "checkpoint", sector, bonus, intro: unlock ? unlock.intro : null });
      lastWarning = 99;
      newMission();
    }

    state.timeLeft = Math.max(0, state.timeLeft - dt);
    const whole = Math.ceil(state.timeLeft);
    if (whole <= 10 && whole < lastWarning && whole > 0) {
      lastWarning = whole;
      if (whole === 10 || whole <= 5) state.events.push({ type: "time-warning", seconds: whole });
    }
    if (state.timeLeft <= 0) {
      state.over = true;
      state.endReason = "time";
      state.events.push({ type: "timeout" });
      return;
    }

    // Spawning. Traffic pulls out on the beat; hazards come by distance.
    if (audio.beat) {
      beatCount += 1;
      trafficBudget += trafficDensity() + audio.energy * 0.15;
      if (trafficBudget >= 1 && state.time > 0.3) {
        if (spawnCar()) trafficBudget -= 1;
        else trafficBudget = Math.min(trafficBudget, 1.5);
      } else if (beatCount % 3 === 0) spawnNotes();
    }
    if (state.position > 0 && spawnZ() >= nextHazardZ) {
      spawnHazard();
      nextHazardZ = spawnZ() + hazardGap();
    }
    for (let i = pending.length - 1; i >= 0; i--) {
      if (pending[i].z <= spawnZ()) {
        const item = pending[i];
        pending.splice(i, 1);
        item.run();
      }
    }

    const playerZ = state.position + PLAYER_AHEAD;
    updateTraffic(dt, playerZ);

    // Pedestrians walk across and wait at the edge of the player's lane
    // rather than step out in front of the car.
    for (const hazard of state.hazards) {
      if (hazard.kind === "pedestrian") {
        if (hazard.hit) {
          hazard.x += hazard.vx * dt;
          continue;
        }
        const next = hazard.x + hazard.vx * dt;
        if (Math.abs(hazard.x) > 1.2 && Math.sign(hazard.x) === Math.sign(hazard.vx)) continue;
        // Nobody steps into the path of a car that is about to arrive, the
        // player's or anyone else's; they wait at the edge of that lane.
        const steppingIn = (x: number, halfWidth: number, z: number, speed: number) => {
          const eta = (hazard.z - z) / Math.max(speed, 1);
          const reach = halfWidth + hazard.halfWidth + 0.12;
          return eta > -0.25 && eta < 1.3 && Math.abs(next - x) < reach && Math.abs(hazard.x - x) >= reach;
        };
        let wait = steppingIn(state.playerX, PLAYER_SIZE.halfWidth, playerZ, state.speed);
        for (const car of state.cars) if (!wait && car.z < hazard.z) wait = steppingIn(car.x, car.halfWidth, car.z + car.halfLength, car.speed);
        hazard.waiting = wait;
        if (!wait) {
          hazard.x = next;
          hazard.phase += dt * 7;
        }
      } else if (hazard.kind === "cone" && hazard.hit) {
        hazard.x += hazard.vx * dt;
        hazard.z += hazard.vz * dt;
        hazard.vz *= Math.max(0, 1 - dt * 2);
        hazard.lift = Math.max(0, hazard.lift + dt * 3 - hazard.phase * dt);
        hazard.phase += dt * 12;
      }
    }

    // The player against everything on the road.
    const player = playerFootprint(state);
    for (const car of state.cars) {
      const dz = car.z - player.z;
      const reach = car.halfLength + PLAYER_SIZE.halfLength;
      // Swerving out from behind a car with under CLOSE_CALL seconds to spare.
      const inLine = dz > 0 && !car.hit && Math.abs(car.x - state.playerX) < car.halfWidth + PLAYER_SIZE.halfWidth;
      if (car.ahead && !inLine && dz > 0) {
        const closing = state.speed - car.speed;
        const spare = (dz - reach) / closing;
        if (closing > 500 && spare < CLOSE_CALL) {
          car.closeCall = true;
          car.closeTtc = Math.max(0, spare);
        }
      }
      car.ahead = inLine;
      if (Math.abs(dz) < reach && !car.hit) {
        const gapSide = Math.abs(car.x - state.playerX) - car.halfWidth - PLAYER_SIZE.halfWidth;
        car.closest = Math.min(car.closest, gapSide);
        if (state.invulnerable <= 0 && touches(car, car.yaw)) {
          car.hit = true;
          const side = Math.sign(car.x - state.playerX) || 1;
          if (Math.abs(dz) < reach * 0.55) {
            // Side swipe: both cars bounce apart.
            car.knock = side * 1.1;
            state.slideVel = -side * 0.9;
            state.slide = 0.35;
          }
          crash("car");
          continue;
        }
      }
      if (!car.passed && car.z + car.halfLength < player.z - PLAYER_SIZE.halfLength) {
        car.passed = true;
        if (car.hit) continue;
        state.passedCars += 1;
        state.combo += 1;
        state.score += 20 * multiplier();
        advanceMission("pass");
        if (!car.nearMissed && (car.closest < 0.1 || car.closeCall)) {
          car.nearMissed = true;
          state.nearMisses += 1;
          // The later the swerve, the bigger the close call.
          const gain = Math.round((car.closeCall ? 150 + 250 * (1 - car.closeTtc / CLOSE_CALL) : 80) * multiplier());
          state.score += gain;
          state.nitro = Math.min(1, state.nitro + (car.closeCall ? 0.25 : 0.15));
          state.events.push({ type: "near-miss", gain, side: Math.sign(car.x - state.playerX), close: car.closeCall });
          advanceMission("near");
        }
      }
    }

    for (const hazard of state.hazards) {
      if (hazard.hit) continue;
      if (Math.abs(hazard.z - player.z) > hazard.halfLength + PLAYER_SIZE.halfLength + 200) continue;
      if (!touches(hazard)) continue;
      if (hazard.kind === "slick") {
        hazard.hit = true;
        if (state.invulnerable > 0 || state.slide > 0) continue;
        state.slide = 1.1;
        state.slideVel = (rng() < 0.5 ? -1 : 1) * (0.5 + rng() * 0.15);
        state.events.push({ type: "slide" });
        continue;
      }
      if (state.invulnerable > 0) continue;
      hazard.hit = true;
      if (hazard.kind === "cone") {
        // A cone costs speed and the combo, not a life.
        const side = Math.sign(hazard.x - state.playerX) || 1;
        hazard.vx = side * 1.4;
        hazard.vz = state.speed * 0.7;
        hazard.phase = 0;
        state.speed *= 0.7;
        state.combo = 0;
        state.bumps += 1;
        hitThisSector = true;
        if (state.mission?.kind === "clean") failMission();
        state.events.push({ type: "bump" });
      } else if (hazard.kind === "pedestrian") {
        // They jump back to the kerb they came from.
        hazard.vx = (hazard.x < state.playerX ? -1 : 1) * 2.4;
        for (const walk of state.crosswalks) if (walk.id === hazard.group) walk.hit = true;
        crash("pedestrian");
      } else {
        if (hazard.kind === "roadworks" && state.mission?.kind === "roadworks") failMission();
        crash(hazard.kind === "roadworks" ? "roadworks" : "barrier");
      }
    }

    // Roadworks and crosswalks behind the player count for the missions.
    for (const hazard of state.hazards) {
      if (hazard.kind === "roadworks" && hazard.group === 0 && hazard.z + hazard.halfLength < player.z - PLAYER_SIZE.halfLength) {
        hazard.group = 1;
        if (!hazard.hit) advanceMission("roadworks");
      }
    }
    for (const walk of state.crosswalks) {
      if (!walk.done && walk.z < player.z - PLAYER_SIZE.halfLength) {
        walk.done = true;
        if (!walk.hit) advanceMission("crosswalk");
      }
    }

    for (const pickup of state.pickups) {
      if (pickup.taken) continue;
      if (Math.abs(pickup.z - player.z) < PLAYER_SIZE.halfLength + 80 && Math.abs(pickup.x - state.playerX) < PLAYER_SIZE.halfWidth + 0.1) {
        pickup.taken = true;
        state.combo += 1;
        state.notes += 1;
        const gain = 40 * multiplier();
        state.score += gain;
        state.nitro = Math.min(1, state.nitro + 0.2);
        state.events.push({ type: "pickup", gain, x: pickup.x });
        advanceMission("notes");
      }
    }

    if (state.mission?.kind === "speed" && !state.mission.done) {
      speedStreak = kmhFor(state.speed) >= state.mission.kmh ? speedStreak + dt : 0;
      state.mission.progress = Math.min(state.mission.target, Math.floor(speedStreak * 10) / 10);
      if (speedStreak >= state.mission.target) completeMission();
    }

    // Forget what is behind the camera.
    const behind = state.position - SEGMENT * 16;
    // Cars that drove off into the fog ahead are gone too.
    const beyond = state.position + SPAWN_AHEAD + 6000;
    state.cars = state.cars.filter((car) => car.z + car.halfLength > behind && car.z - car.halfLength < beyond);
    state.pickups = state.pickups.filter((pickup) => pickup.z > behind && !pickup.taken);
    state.hazards = state.hazards.filter((hazard) => hazard.z + hazard.halfLength > behind && Math.abs(hazard.x) < 3);
    state.signs = state.signs.filter((sign) => sign.z > behind);
    state.crosswalks = state.crosswalks.filter((walk) => walk.z > behind);
    while (walkPlan.length && walkPlan[0] < behind) walkPlan.shift();
    for (let i = closures.length - 1; i >= 0; i--) if (closures[i][1] < behind) closures.splice(i, 1);

    state.score += (state.position - before) * 0.0012 * (state.boost > 0 ? 2 : 1);
  };

  // --- The demo driver ----------------------------------------------------------
  // Tries a handful of plans every frame (which lane, change now or a moment
  // later, keep the speed or brake first), plays each one forward a couple of
  // seconds against where every car, cone and person will be, and drives the
  // plan that stays clear the longest and gets furthest. Notes are a bonus,
  // slicks a cost, cones cheaper to hit than cars or people.
  const MAX_OBJECTS = 72;
  const objZ = new Float64Array(MAX_OBJECTS);
  const objV = new Float64Array(MAX_OBJECTS);
  const objA = new Float64Array(MAX_OBJECTS);
  const objX = new Float64Array(MAX_OBJECTS);
  const objTX = new Float64Array(MAX_OBJECTS);
  const objVX = new Float64Array(MAX_OBJECTS);
  const objHW = new Float64Array(MAX_OBJECTS);
  const objHL = new Float64Array(MAX_OBJECTS);
  const objSkew = new Float64Array(MAX_OBJECTS);
  // 0 car, 1 cone, 2 slick, 3 note, 4 anything else solid
  const objKind = new Uint8Array(MAX_OBJECTS);
  const touched = new Uint8Array(MAX_OBJECTS);
  const inLine = new Uint8Array(MAX_OBJECTS);
  // Fine steps for the first second, where a miss decides a crash, coarse after.
  const PLAN_STEPS = 34;
  // The lane the driver last decided on. Sticking to it unless something is
  // clearly better keeps it from dithering between two equal gaps.
  let committed = 1;
  const PLAN_HORIZON = 2.4;
  const planStep = (step: number) => (step < 20 ? 0.05 : 0.1);
  const DELAYS = [0, 0.1, 0.2, 0.3, 0.45, 0.6, 1, 1.5];
  const BRAKES = [0, 0.5, 1.2];

  const simulatePlan = (count: number, lane: number, delay: number, brake: number, boost: boolean, from: number) => {
    const pl = PLAYER_SIZE.halfLength;
    const pw = PLAYER_SIZE.halfWidth;
    let x = state.playerX;
    let v = state.speed;
    let travelled = 0;
    let t = 0;
    let bonus = 0;
    const warmUp = Math.max(0, 4 - state.sector) * 550;
    let slide = state.slide;
    let slideVel = state.slideVel;
    const stunned = state.stun;
    touched.fill(0, 0, count);
    for (let i = 0; i < count; i++) inLine[i] = objKind[i] === 0 && objZ[i] > 0 && Math.abs(objX[i] - x) < objHW[i] + pw ? 1 : 0;
    const cruise = levelSpeed() * (boost || state.boost > 0 ? 1.4 : 1);
    for (let step = 0; step < PLAN_STEPS; step++) {
      const dt = planStep(step);
      t += dt;
      const target = t <= brake ? 0 : cruise;
      v += (target - v) * Math.min(1, dt * (t <= brake ? 2.2 : boost ? 2.4 : 1));
      travelled += v * dt;
      const aim = t > delay ? LANES[lane] : LANES[from];
      const steer = t < stunned ? 0 : clamp((aim - x) * 4, -1, 1) * (slide > 0 ? 0.45 : 1);
      x += steer * dt * 2.2 * Math.min(1, v / CRUISE + 0.25);
      if (slide > 0) {
        slide -= dt;
        x += slideVel * dt;
        slideVel *= Math.max(0, 1 - dt * 1.6);
      }
      for (let i = 0; i < count; i++) {
        const speed = Math.max(0, objV[i] + objA[i] * t);
        const oz = objZ[i] + (objA[i] < 0 && speed === 0 && objA[i] !== 0 ? (objV[i] * objV[i]) / (-2 * objA[i]) : (objV[i] + speed) * 0.5 * t);
        const dz = oz - travelled;
        const kind = objKind[i];
        if (kind === 0 && inLine[i] === 1) {
          // Worth a close call if the swerve leaves it late but clean.
          const movedNow = objTX[i] - objX[i];
          const lx = objX[i] + Math.sign(movedNow) * Math.min(Math.abs(movedNow), 0.7 * Math.max(0.3, Math.min(1, objV[i] / 3000)) * t);
          if (Math.abs(lx - x) >= objHW[i] + pw) {
            inLine[i] = 0;
            const closing = v - speed;
            const spare = (dz - objHL[i] - pl) / Math.max(closing, 1);
            // It warms up to close calls once the first sectors are behind it.
            // The later the swerve, the more it is worth, as in the game.
            if (closing > 500 && dz > 0 && spare < CLOSE_CALL) bonus += state.sector >= 3 ? 1.5 + 3 * (1 - spare / CLOSE_CALL) : 0.5;
          }
        }
        // The margin covers the corner a turning car swings back.
        // Ahead, the nose of a turning car also reaches further forward.
        // In the first sectors it leaves extra room, like someone still warming up.
        if (Math.abs(dz) > objHL[i] + pl + (kind === 0 || kind === 4 ? (dz > 0 ? 520 + warmUp : 380) : 150)) continue;
        const moved = objTX[i] - objX[i];
        const ox = objX[i] + Math.sign(moved) * Math.min(Math.abs(moved), 0.7 * Math.max(0.3, Math.min(1, objV[i] / 3000)) * t) + objVX[i] * t;
        // A turning car is wider by its angle.
        if (Math.abs(ox - x) >= objHW[i] + objSkew[i] + pw + (kind === 0 || kind === 4 ? 0.04 : kind === 1 ? 0.05 : 0.02)) continue;
        if (kind === 3) {
          if (!touched[i]) bonus += 1;
          touched[i] = 1;
        } else if (kind === 2) {
          if (!touched[i]) bonus -= 4;
          touched[i] = 1;
        } else {
          // A hit soon is a disaster; one near the end of the look-ahead only
          // means this plan needs another change later.
          if (t > 1) return (travelled + v * (PLAN_HORIZON - t)) * 0.0005 + bonus - (PLAN_HORIZON - t) * 0.8 - 0.5;
          return (kind === 1 ? -400 : -1000) + t * 100;
        }
      }
    }
    return travelled * 0.0005 + bonus;
  };

  const autopilot = (audio?: AudioFrame): DriveControls => {
    const player = playerFootprint(state);
    let count = 0;
    const add = (z: number, v: number, a: number, x: number, tx: number, vx: number, hw: number, hl: number, kind: number, skew = 0) => {
      if (count >= MAX_OBJECTS) return;
      objSkew[count] = skew;
      objZ[count] = z - player.z;
      objV[count] = v;
      objA[count] = a;
      objX[count] = x;
      objTX[count] = tx;
      objVX[count] = vx;
      objHW[count] = hw;
      objHL[count] = hl;
      objKind[count] = kind;
      count += 1;
    };
    const reach = state.speed * 2.6 + 4000;
    for (const car of state.cars) {
      if (car.hit || car.z - player.z > reach || car.z - player.z < -4000) continue;
      // A blinking indicator counts as already moving over.
      add(car.z, car.speed, car.accel < -400 ? car.accel : 0, car.x, car.signal > 0 ? LANES[car.signalLane] : car.targetX, 0, car.halfWidth, car.halfLength, 0, Math.abs(Math.sin(car.yaw)) * (car.halfLength / ROAD_WIDTH));
    }
    for (const hazard of state.hazards) {
      if (hazard.hit || hazard.z - player.z > reach + hazard.halfLength || hazard.z + hazard.halfLength - player.z < -1500) continue;
      const kind = hazard.kind === "cone" ? 1 : hazard.kind === "slick" ? 2 : 4;
      // People get a little extra room; someone waiting stays put.
      const extra = hazard.kind === "pedestrian" ? 0.1 : 0;
      add(hazard.z, 0, 0, hazard.x, hazard.x, hazard.waiting ? 0 : hazard.vx, hazard.halfWidth + extra, hazard.halfLength, kind);
    }
    for (const pickup of state.pickups) {
      if (pickup.taken || pickup.z - player.z > reach || pickup.z < player.z) continue;
      add(pickup.z, 0, 0, pickup.x, pickup.x, 0, 0.1, 80, 3);
    }

    let current = 0;
    for (let lane = 1; lane < LANES.length; lane++) if (Math.abs(LANES[lane] - state.playerX) < Math.abs(LANES[current] - state.playerX)) current = lane;
    let bestScore = -Infinity;
    let bestLane = current;
    let bestDelay = 0;
    let bestBrake = 0;
    for (let lane = 0; lane < LANES.length; lane++) {
      for (const delay of DELAYS) {
        if (lane === current && delay > 0) continue;
        for (const brake of BRAKES) {
          // The long brake is for dropping back out of a jam.
          if (brake > 1 && delay > 0 && delay < 1) continue;
          let score = simulatePlan(count, lane, delay, brake, false, current);
          score -= (lane === current ? 0 : 0.25 + Math.abs(lane - current) * 0.1) + delay * 0.3 + brake * 0.8;
          if (lane === committed) score += 0.6;
          if (score > bestScore) {
            bestScore = score;
            bestLane = lane;
            bestDelay = delay;
            bestBrake = brake;
          }
        }
      }
    }
    committed = bestLane;
    // Swerve on the beat when the next one is close and waiting for it is
    // still safe: that is worth points, and it looks like driving to the song.
    let waitForBeat = false;
    if (audio && bestLane !== current && bestDelay === 0 && audio.nextBeatIn < 450 && audio.sinceBeat > BEAT_WINDOW) {
      // Only when there is slack to spare beyond the wait, so it never turns
      // a comfortable swerve into a tight one.
      const wait = audio.nextBeatIn / 1000;
      const waiting = simulatePlan(count, bestLane, wait, bestBrake, false, current) - (0.25 + Math.abs(bestLane - current) * 0.1) - bestBrake * 0.8 + (bestLane === committed ? 0.6 : 0);
      waitForBeat = waiting > bestScore - 1.2 && simulatePlan(count, bestLane, wait + 0.2, bestBrake, false, current) > -100;
    }
    const aim = bestDelay > 0 || waitForBeat ? LANES[current] : LANES[bestLane];
    const safe = bestScore > -100;
    const boost =
      state.nitro >= 1 &&
      state.boost <= 0 &&
      safe &&
      bestBrake === 0 &&
      simulatePlan(count, bestLane, bestDelay, 0, true, current) > -100 &&
      !state.crosswalks.some((walk) => walk.z > player.z && walk.z - player.z < state.speed * 3.5);
    return { steer: 0, aim, boost, brake: bestBrake > 0 };
  };

  return {
    state,
    curveAt,
    hillAt,
    archAt,
    update,
    autopilot,
    missionLabel,
    kmhFor,
    speedKmh: () => kmhFor(state.speed),
    levelSpeed,
    // How hard the road is right now: speed times how much is thrown at you.
    intensity: () => (levelSpeed() / CRUISE) * 40 * (0.3 + trafficDensity() + 3600 / hazardGapBase()),
  };
}

export const missionRewardLabel = (reward: MissionReward) => REWARD_LABEL[reward];
